import React, { useMemo } from 'react';
import styles from './InsuranceReportManager.module.scss';
import { TermItem, useTermItemsQuery } from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import InsuranceCompanyOperations from '../InsuranceCompanyOperations/InsuranceCompanyOperations';
import useRootWorkplace from '../../../Manager/Hooks/useRootWorkplace';
import { useAppContext } from '../../../../Services/AppContext';
import NavigationStepper, {
	NavigationStepperItemType,
} from '../../../../Components/Stepper/NavigationStepper';
import ManagerInsuranceReportList from '../ManagerInsuranceReportList/ManagerInsuranceReportList';
import InsuranceExportsList from '../InsuranceExports/InsuranceExportsList';

interface SettingsProps {
	items: TermItem[];
	rootWorkplaceId: string;
}

const Settings: React.FC<SettingsProps> = ({ items, rootWorkplaceId }) => {
	return (
		<div className={styles.wrapper}>
			{items?.map((item, key) => {
				if (item?.name && item?.id) {
					return (
						<React.Fragment key={key}>
							<InsuranceCompanyOperations
								name={item.name}
								insuranceId={item.id}
								rootWorkplaceId={rootWorkplaceId}
							/>
						</React.Fragment>
					);
				}
			})}
		</div>
	);
};

const InsuranceReportManager: React.FC = () => {
	const { workplaceId, userData } = useAppContext();
	const { data, isLoading } = useTermItemsQuery({
		termType: 'insurance_companies',
	});
	const { rootWorkplaceId } = useRootWorkplace(workplaceId, userData.id);

	const steps: NavigationStepperItemType[] = useMemo(() => {
		if (rootWorkplaceId) {
			return [
				{
					key: 'exportedBadges',
					name: 'component.insuranceReportManager.exportedBadges',
					component: () => (
						<InsuranceExportsList
							workplaceId={workplaceId}
							rootWorkplaceId={rootWorkplaceId}
						/>
					),
				},
				{
					key: 'insuranceReport',
					name: 'base.insuranceReports',
					component: () => (
						<ManagerInsuranceReportList workplaceId={workplaceId} />
					),
				},
				{
					key: 'settings',
					name: 'component.insuranceReportManager.settings',
					component: () => (
						<Settings
							items={data?.termItems?.data as TermItem[]}
							rootWorkplaceId={rootWorkplaceId}
						/>
					),
				},
			];
		}

		return [];
	}, [data, rootWorkplaceId]);

	if (isLoading || !rootWorkplaceId) {
		return <Loading />;
	}

	return (
		<NavigationStepper
			steps={steps}
			initialStepPath='exportedBadges'
			canSkip
			showStepNumbers={false}
		/>
	);
};

export default InsuranceReportManager;
